import { Fragment, type Node as ProseMirrorNode } from "prosemirror-model";
import {
  type EditorState,
  Plugin,
  PluginKey,
  TextSelection,
  type Transaction,
} from "prosemirror-state";
import {
  addColumnAfter,
  addColumnBefore,
  addRowAfter,
  addRowBefore,
  CellSelection,
  deleteColumn,
  deleteRow,
  deleteTable,
  isInTable,
  selectedRect,
  TableMap,
  type TableRect,
} from "prosemirror-tables";
import type { EditorView } from "prosemirror-view";
import type { CSSProperties, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createRoot, type Root } from "react-dom/client";
import {
  LuArrowDown,
  LuArrowLeft,
  LuArrowRight,
  LuArrowUp,
  LuTrash,
} from "react-icons/lu";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/utils/lib";

const tableControlsKey = new PluginKey("editor-v3-table-controls");

type TableCommand = (
  state: EditorState,
  dispatch?: (tr: Transaction) => void,
) => boolean;

type TableControlType = "column" | "corner" | "row";

type TableControlModel = {
  controls: TableSelectorControl[];
};

type TableControlBox = {
  centerX: number;
  centerY: number;
  height: number;
  left: number;
  top: number;
  width: number;
};

type TableControlStyle = CSSProperties & {
  "--editor-v3-table-control-drag-x"?: string;
  "--editor-v3-table-control-drag-y"?: string;
};

type TableSelectorControl = {
  active: boolean;
  box: TableControlBox;
  draggable: boolean;
  id: string;
  index: number;
  label: string;
  onSelect: () => void;
  style: TableControlStyle;
  type: TableControlType;
};

type TableDragState = {
  active: boolean;
  controlId: string;
  currentX: number;
  currentY: number;
  element: HTMLElement;
  pointerId: number;
  sourceIndex: number;
  startX: number;
  startY: number;
  targetIndex: number;
  type: Extract<TableControlType, "column" | "row">;
};

const runTableCommand = (view: EditorView, command: TableCommand) => {
  command(view.state, view.dispatch);
  view.focus();
};

const cellPosition = (rect: TableRect, row: number, column: number) =>
  rect.tableStart + rect.map.positionAt(row, column, rect.table);

const selectTable = (view: EditorView, rect: TableRect) => {
  const firstCell = cellPosition(rect, 0, 0);
  const lastCell = cellPosition(rect, rect.map.height - 1, rect.map.width - 1);

  view.dispatch(
    view.state.tr.setSelection(
      CellSelection.create(view.state.doc, firstCell, lastCell),
    ),
  );
  view.focus();
};

const selectRow = (view: EditorView, rect: TableRect, row: number) => {
  const firstCell = cellPosition(rect, row, 0);
  const lastCell = cellPosition(rect, row, rect.map.width - 1);

  view.dispatch(
    view.state.tr.setSelection(
      CellSelection.rowSelection(
        view.state.doc.resolve(firstCell),
        view.state.doc.resolve(lastCell),
      ),
    ),
  );
  view.focus();
};

const selectColumn = (view: EditorView, rect: TableRect, column: number) => {
  const firstCell = cellPosition(rect, 0, column);
  const lastCell = cellPosition(rect, rect.map.height - 1, column);

  view.dispatch(
    view.state.tr.setSelection(
      CellSelection.colSelection(
        view.state.doc.resolve(firstCell),
        view.state.doc.resolve(lastCell),
      ),
    ),
  );
  view.focus();
};

const collapseTableSelection = (view: EditorView) => {
  if (!(view.state.selection instanceof CellSelection)) {
    return;
  }

  const rect = getTableRect(view.state);

  if (!rect) {
    return;
  }

  const firstCell = cellPosition(rect, rect.top, rect.left);

  view.dispatch(
    view.state.tr.setSelection(
      TextSelection.near(view.state.doc.resolve(firstCell + 1)),
    ),
  );
  view.focus();
};

const isSimpleTable = (rect: TableRect) => {
  for (let rowIndex = 0; rowIndex < rect.table.childCount; rowIndex += 1) {
    const row = rect.table.child(rowIndex);

    if (row.childCount !== rect.map.width) {
      return false;
    }

    for (let columnIndex = 0; columnIndex < row.childCount; columnIndex += 1) {
      const cell = row.child(columnIndex);

      if (cell.attrs.colspan !== 1 || cell.attrs.rowspan !== 1) {
        return false;
      }
    }
  }

  return true;
};

const reorderItems = <T,>(
  items: T[],
  sourceIndex: number,
  targetIndex: number,
) => {
  const next = items.slice();
  const [item] = next.splice(sourceIndex, 1);

  if (!item) {
    return next;
  }

  next.splice(targetIndex, 0, item);
  return next;
};

const normalizeMarkdownTableRows = (rows: ProseMirrorNode[]) => {
  const { table_cell, table_header } = rows[0]?.type.schema.nodes ?? {};

  if (!table_cell || !table_header) {
    return rows;
  }

  return rows.map((row, rowIndex) => {
    const targetCellType = rowIndex === 0 ? table_header : table_cell;
    const cells: ProseMirrorNode[] = [];

    row.forEach((cell) => {
      cells.push(
        cell.type === targetCellType
          ? cell
          : targetCellType.create(cell.attrs, cell.content),
      );
    });

    return row.copy(Fragment.fromArray(cells));
  });
};

const selectMovedRow = (
  tr: Transaction,
  tableStart: number,
  table: ProseMirrorNode,
  row: number,
) => {
  const map = TableMap.get(table);
  const firstCell = tableStart + map.positionAt(row, 0, table);
  const lastCell = tableStart + map.positionAt(row, map.width - 1, table);

  return tr.setSelection(
    CellSelection.rowSelection(
      tr.doc.resolve(firstCell),
      tr.doc.resolve(lastCell),
    ),
  );
};

const selectMovedColumn = (
  tr: Transaction,
  tableStart: number,
  table: ProseMirrorNode,
  column: number,
) => {
  const map = TableMap.get(table);
  const firstCell = tableStart + map.positionAt(0, column, table);
  const lastCell = tableStart + map.positionAt(map.height - 1, column, table);

  return tr.setSelection(
    CellSelection.colSelection(
      tr.doc.resolve(firstCell),
      tr.doc.resolve(lastCell),
    ),
  );
};

const replaceTable = (
  view: EditorView,
  rect: TableRect,
  table: ProseMirrorNode,
  selectMovedArea: (tr: Transaction) => Transaction,
) => {
  const tr = view.state.tr.replaceWith(
    rect.tableStart - 1,
    rect.tableStart - 1 + rect.table.nodeSize,
    table,
  );

  view.dispatch(selectMovedArea(tr).scrollIntoView());
  view.focus();
};

const moveTableRow = (
  view: EditorView,
  sourceIndex: number,
  targetIndex: number,
) => {
  const rect = getTableRect(view.state);

  if (
    !rect ||
    sourceIndex === targetIndex ||
    sourceIndex < 0 ||
    targetIndex < 0 ||
    sourceIndex >= rect.map.height ||
    targetIndex >= rect.map.height ||
    !isSimpleTable(rect)
  ) {
    return false;
  }

  const rows: ProseMirrorNode[] = [];
  rect.table.forEach((row) => rows.push(row));

  const nextRows = normalizeMarkdownTableRows(
    reorderItems(rows, sourceIndex, targetIndex),
  );
  const nextTable = rect.table.copy(Fragment.fromArray(nextRows));

  replaceTable(view, rect, nextTable, (tr) =>
    selectMovedRow(tr, rect.tableStart, nextTable, targetIndex),
  );
  return true;
};

const moveTableColumn = (
  view: EditorView,
  sourceIndex: number,
  targetIndex: number,
) => {
  const rect = getTableRect(view.state);

  if (
    !rect ||
    sourceIndex === targetIndex ||
    sourceIndex < 0 ||
    targetIndex < 0 ||
    sourceIndex >= rect.map.width ||
    targetIndex >= rect.map.width ||
    !isSimpleTable(rect)
  ) {
    return false;
  }

  const rows: ProseMirrorNode[] = [];
  rect.table.forEach((row) => {
    const cells: ProseMirrorNode[] = [];
    row.forEach((cell) => cells.push(cell));
    rows.push(
      row.copy(
        Fragment.fromArray(reorderItems(cells, sourceIndex, targetIndex)),
      ),
    );
  });

  const nextRows = normalizeMarkdownTableRows(rows);
  const nextTable = rect.table.copy(Fragment.fromArray(nextRows));

  replaceTable(view, rect, nextTable, (tr) =>
    selectMovedColumn(tr, rect.tableStart, nextTable, targetIndex),
  );
  return true;
};

const moveTableSelection = (
  view: EditorView,
  type: TableDragState["type"],
  sourceIndex: number,
  targetIndex: number,
) => {
  if (type === "row") {
    return moveTableRow(view, sourceIndex, targetIndex);
  }

  return moveTableColumn(view, sourceIndex, targetIndex);
};

const getTableRect = (state: EditorState) => {
  if (!isInTable(state)) {
    return null;
  }

  try {
    return selectedRect(state);
  } catch {
    return null;
  }
};

const tableDomFromRect = (view: EditorView, rect: TableRect) => {
  const dom = view.nodeDOM(rect.tableStart - 1);

  if (dom instanceof HTMLTableElement) {
    return dom;
  }

  if (dom instanceof HTMLElement) {
    return (
      dom.closest("table") ??
      dom.querySelector("table") ??
      view.dom.querySelector("table")
    );
  }

  return view.dom.querySelector("table");
};

const tableControlStyle = (
  editorBox: DOMRect,
  box: {
    left: number;
    top: number;
  },
): TableControlStyle => ({
  left: `${box.left - editorBox.left}px`,
  top: `${box.top - editorBox.top}px`,
});

const tableControlBox = ({
  height,
  left,
  top,
  width,
}: {
  height: number;
  left: number;
  top: number;
  width: number;
}): TableControlBox => ({
  centerX: left + width / 2,
  centerY: top + height / 2,
  height,
  left,
  top,
  width,
});

const selectedControlType = (
  rect: TableRect,
  selection: EditorState["selection"],
) => {
  if (!(selection instanceof CellSelection)) {
    return null;
  }

  const wholeTable =
    rect.left === 0 &&
    rect.top === 0 &&
    rect.right === rect.map.width &&
    rect.bottom === rect.map.height;

  if (wholeTable) {
    return "corner";
  }

  if (selection.isRowSelection()) {
    return "row";
  }

  if (selection.isColSelection()) {
    return "column";
  }

  return null;
};

const selectedControlIndex = (
  rect: TableRect,
  type: TableControlType | null,
) => {
  if (type === "row") {
    return rect.top;
  }

  if (type === "column") {
    return rect.left;
  }

  return 0;
};

const createControl = ({
  box,
  draggable,
  editorBox,
  index,
  label,
  onSelect,
  selectedIndex,
  selectedType,
  styleBox,
  type,
}: {
  box: TableControlBox;
  draggable?: boolean;
  editorBox: DOMRect;
  index: number;
  label: string;
  onSelect: () => void;
  selectedIndex: number;
  selectedType: TableControlType | null;
  styleBox: {
    left: number;
    top: number;
  };
  type: TableControlType;
}): TableSelectorControl => ({
  active: selectedType === type && selectedIndex === index,
  box,
  draggable: Boolean(draggable),
  id: `${type}-${index}`,
  index,
  label,
  onSelect,
  style: tableControlStyle(editorBox, styleBox),
  type,
});

const createModel = (
  view: EditorView,
  overlayHost: HTMLElement,
): TableControlModel | null => {
  const rect = getTableRect(view.state);

  if (!rect) {
    return null;
  }

  const table = tableDomFromRect(view, rect);

  if (!table) {
    return null;
  }

  const tableBox = table.getBoundingClientRect();
  const editorBox = overlayHost.getBoundingClientRect();
  const selectedType = selectedControlType(rect, view.state.selection);
  const selectedIndex = selectedControlIndex(rect, selectedType);
  const simpleTable = isSimpleTable(rect);
  const controls: TableSelectorControl[] = [];

  controls.push(
    createControl({
      box: tableControlBox({
        height: tableBox.height || 1,
        left: tableBox.left,
        top: tableBox.top,
        width: tableBox.width || 1,
      }),
      editorBox,
      index: 0,
      label: "Select table",
      onSelect: () => selectTable(view, rect),
      selectedIndex,
      selectedType,
      styleBox: {
        left: tableBox.left,
        top: tableBox.top,
      },
      type: "corner",
    }),
  );

  const firstRow = table.rows[0];

  if (firstRow) {
    Array.from(firstRow.cells).forEach((cell, index) => {
      const cellBox = cell.getBoundingClientRect();
      const fallbackWidth = tableBox.width / Math.max(1, firstRow.cells.length);

      controls.push(
        createControl({
          box: tableControlBox({
            height: tableBox.height || 1,
            left: cellBox.left || tableBox.left + fallbackWidth * index,
            top: tableBox.top,
            width: cellBox.width || fallbackWidth || 1,
          }),
          draggable: simpleTable && firstRow.cells.length > 1,
          editorBox,
          index,
          label: `Select column ${index + 1}`,
          onSelect: () => selectColumn(view, rect, index),
          selectedIndex,
          selectedType,
          styleBox: {
            left:
              (cellBox.left || tableBox.left + fallbackWidth * index) +
              (cellBox.width || fallbackWidth || 1) / 2,
            top: tableBox.top,
          },
          type: "column",
        }),
      );
    });
  }

  Array.from(table.rows).forEach((row, index) => {
    const rowBox = row.getBoundingClientRect();
    const fallbackHeight = tableBox.height / Math.max(1, table.rows.length);

    controls.push(
      createControl({
        box: tableControlBox({
          height: rowBox.height || fallbackHeight || 1,
          left: tableBox.left,
          top: rowBox.top || tableBox.top + fallbackHeight * index,
          width: tableBox.width || 1,
        }),
        draggable: simpleTable && table.rows.length > 1,
        editorBox,
        index,
        label: `Select row ${index + 1}`,
        onSelect: () => selectRow(view, rect, index),
        selectedIndex,
        selectedType,
        styleBox: {
          left: tableBox.left,
          top:
            (rowBox.top || tableBox.top + fallbackHeight * index) +
            (rowBox.height || fallbackHeight || 1) / 2,
        },
        type: "row",
      }),
    );
  });

  return {
    controls,
  };
};

const tableActions = (actionType: TableControlType | null) => {
  if (actionType === "corner") {
    return [
      {
        command: deleteTable,
        icon: <LuTrash data-icon="inline-start" />,
        label: "Delete table",
        variant: "destructive" as const,
      },
    ];
  }

  if (actionType === "row") {
    return [
      {
        command: addRowBefore,
        icon: <LuArrowUp data-icon="inline-start" />,
        label: "Insert row before",
      },
      {
        command: addRowAfter,
        icon: <LuArrowDown data-icon="inline-start" />,
        label: "Insert row after",
      },
      {
        command: deleteRow,
        icon: <LuTrash data-icon="inline-start" />,
        label: "Delete row",
        variant: "destructive" as const,
      },
    ];
  }

  if (actionType === "column") {
    return [
      {
        command: addColumnBefore,
        icon: <LuArrowLeft data-icon="inline-start" />,
        label: "Insert column before",
      },
      {
        command: addColumnAfter,
        icon: <LuArrowRight data-icon="inline-start" />,
        label: "Insert column after",
      },
      {
        command: deleteColumn,
        icon: <LuTrash data-icon="inline-start" />,
        label: "Delete column",
        variant: "destructive" as const,
      },
    ];
  }

  return [];
};

const stopEditorMouseDown = (
  event: React.MouseEvent<HTMLElement>,
  callback?: () => void,
) => {
  event.preventDefault();
  event.stopPropagation();
  callback?.();
};

const capturePointer = (element: HTMLElement, pointerId: number) => {
  try {
    element.setPointerCapture?.(pointerId);
  } catch {
    // Pointer capture can fail when the browser has already released it.
  }
};

const releasePointer = (element: HTMLElement, pointerId: number) => {
  try {
    if (!element.hasPointerCapture || element.hasPointerCapture(pointerId)) {
      element.releasePointerCapture?.(pointerId);
    }
  } catch {
    // Pointer capture may already be released by the browser.
  }
};

const getDragTargetIndex = (
  model: TableControlModel | null,
  drag: TableDragState,
) => {
  const candidates =
    model?.controls.filter(
      (control) => control.type === drag.type && control.draggable,
    ) ?? [];

  if (!candidates.length) {
    return drag.sourceIndex;
  }

  const axis = drag.type === "column" ? drag.currentX : drag.currentY;
  let nearest = candidates[0]!;
  let nearestDistance = Number.POSITIVE_INFINITY;

  candidates.forEach((control) => {
    const center =
      drag.type === "column" ? control.box.centerX : control.box.centerY;
    const distance = Math.abs(axis - center);

    if (distance < nearestDistance) {
      nearest = control;
      nearestDistance = distance;
    }
  });

  return nearest.index;
};

const TableActionButton = ({
  children,
  label,
  onMouseDown,
  variant = "outline",
}: {
  children: ReactNode;
  label: string;
  onMouseDown: () => void;
  variant?: "destructive" | "outline";
}) => (
  <Button
    aria-label={label}
    className="editor-v3-table-action"
    size="icon"
    title={label}
    variant={variant}
    onMouseDown={(event) => stopEditorMouseDown(event, onMouseDown)}
  >
    {children}
  </Button>
);

const TableControlsOverlay = ({
  model,
  overlayHost,
  view,
}: {
  model: TableControlModel | null;
  overlayHost: HTMLElement;
  view: EditorView;
}) => {
  const [openControlId, setOpenControlId] = useState<string | null>(null);
  const [dragState, setDragState] = useState<TableDragState | null>(null);
  const dragStateRef = useRef<TableDragState | null>(null);
  const suppressNextClickRef = useRef(false);
  const controlIds = model?.controls.map((control) => control.id).join("|");

  const updateDragState = useCallback((nextState: TableDragState | null) => {
    dragStateRef.current = nextState;
    setDragState(nextState);
  }, []);

  const handlePointerMove = useCallback(
    (event: {
      clientX: number;
      clientY: number;
      pointerId: number;
      pointerType: string;
      preventDefault: () => void;
      stopPropagation: () => void;
    }) => {
      const drag = dragStateRef.current;

      if (!drag || event.pointerId !== drag.pointerId) {
        return;
      }

      const distance = Math.hypot(
        event.clientX - drag.startX,
        event.clientY - drag.startY,
      );
      const activationDistance = event.pointerType === "touch" ? 8 : 4;

      if (!drag.active && distance < activationDistance) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const nextDrag = {
        ...drag,
        active: true,
        currentX: event.clientX,
        currentY: event.clientY,
      };
      const targetIndex = getDragTargetIndex(model, nextDrag);
      const moved =
        targetIndex !== drag.sourceIndex &&
        moveTableSelection(view, drag.type, drag.sourceIndex, targetIndex);

      updateDragState({
        ...nextDrag,
        controlId: moved ? `${drag.type}-${targetIndex}` : drag.controlId,
        sourceIndex: moved ? targetIndex : drag.sourceIndex,
        targetIndex,
      });
      setOpenControlId(null);
    },
    [model, updateDragState, view],
  );

  const finishPointerDrag = useCallback(
    (
      event: {
        pointerId: number;
        preventDefault: () => void;
        stopPropagation: () => void;
      },
      commit: boolean,
    ) => {
      const drag = dragStateRef.current;

      if (!drag || event.pointerId !== drag.pointerId) {
        return;
      }

      releasePointer(drag.element, event.pointerId);
      updateDragState(null);

      if (!drag.active) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      suppressNextClickRef.current = true;
      window.setTimeout(() => {
        suppressNextClickRef.current = false;
      }, 0);

      void commit;
      collapseTableSelection(view);
    },
    [updateDragState, view],
  );

  useEffect(() => {
    if (
      openControlId &&
      !model?.controls.some((control) => control.id === openControlId)
    ) {
      setOpenControlId(null);
    }
  }, [controlIds, model?.controls, openControlId]);

  useEffect(() => {
    if (
      dragState &&
      !model?.controls.some((control) => control.id === dragState.controlId)
    ) {
      dragStateRef.current = null;
      setDragState(null);
    }
  }, [controlIds, dragState, model?.controls]);

  useEffect(() => {
    if (!dragState) {
      return;
    }

    const handleDocumentPointerMove = (event: PointerEvent) => {
      handlePointerMove(event);
    };
    const handleDocumentPointerCancel = (event: PointerEvent) => {
      finishPointerDrag(event, false);
    };
    const handleDocumentPointerUp = (event: PointerEvent) => {
      finishPointerDrag(event, true);
    };

    document.addEventListener("pointermove", handleDocumentPointerMove, {
      passive: false,
    });
    document.addEventListener("pointercancel", handleDocumentPointerCancel);
    document.addEventListener("pointerup", handleDocumentPointerUp);

    return () => {
      document.removeEventListener("pointermove", handleDocumentPointerMove);
      document.removeEventListener(
        "pointercancel",
        handleDocumentPointerCancel,
      );
      document.removeEventListener("pointerup", handleDocumentPointerUp);
    };
  }, [dragState, finishPointerDrag, handlePointerMove]);

  const handlePointerDown = (
    event: React.PointerEvent<HTMLElement>,
    control: TableSelectorControl,
  ) => {
    if (
      (control.type !== "column" && control.type !== "row") ||
      !control.draggable
    ) {
      return;
    }

    event.stopPropagation();
    capturePointer(event.currentTarget, event.pointerId);
    updateDragState({
      active: false,
      controlId: control.id,
      currentX: event.clientX,
      currentY: event.clientY,
      element: event.currentTarget,
      pointerId: event.pointerId,
      sourceIndex: control.index,
      startX: event.clientX,
      startY: event.clientY,
      targetIndex: control.index,
      type: control.type,
    });
  };

  if (!model) {
    return null;
  }

  return (
    <>
      {model.controls.map((control) => {
        const actions = tableActions(control.type);
        const open = openControlId === control.id;
        const draggingSource =
          dragState?.active && dragState.controlId === control.id;
        const draggingTarget =
          dragState?.active &&
          dragState.type === control.type &&
          dragState.targetIndex === control.index;
        const dragStyle: TableControlStyle | undefined =
          draggingSource && dragState
            ? {
                "--editor-v3-table-control-drag-x":
                  control.type === "column"
                    ? `${dragState.currentX - control.box.centerX}px`
                    : "0px",
                "--editor-v3-table-control-drag-y":
                  control.type === "row"
                    ? `${dragState.currentY - control.box.centerY}px`
                    : "0px",
              }
            : undefined;

        return (
          <Popover
            key={control.id}
            open={open}
            onOpenChange={(nextOpen) => {
              if (nextOpen) {
                setOpenControlId(control.id);
                return;
              }

              setOpenControlId(null);

              if (!dragStateRef.current?.active) {
                collapseTableSelection(view);
              }
            }}
          >
            <PopoverTrigger asChild>
              <Button
                aria-label={control.label}
                className={cn(
                  "editor-v3-table-control",
                  `editor-v3-table-control--${control.type}`,
                  control.active && "editor-v3-table-control--active",
                  open && "editor-v3-table-control--active",
                  draggingSource && "editor-v3-table-control--drag-source",
                  draggingTarget && "editor-v3-table-control--drop-target",
                )}
                aria-grabbed={draggingSource || undefined}
                data-editor-v3-table-control={control.type}
                data-editor-v3-table-draggable={control.draggable || undefined}
                data-editor-v3-table-dragging={draggingSource || undefined}
                data-editor-v3-table-drop-target={draggingTarget || undefined}
                size="xs"
                style={{
                  ...control.style,
                  ...dragStyle,
                }}
                title={control.label}
                variant="ghost"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();

                  if (suppressNextClickRef.current) {
                    suppressNextClickRef.current = false;
                    return;
                  }

                  control.onSelect();

                  if (openControlId === control.id) {
                    setOpenControlId(null);
                    collapseTableSelection(view);
                  } else {
                    setOpenControlId(control.id);
                  }
                }}
                onMouseDown={(event) => stopEditorMouseDown(event)}
                onPointerCancel={(event) => finishPointerDrag(event, false)}
                onPointerDown={(event) => handlePointerDown(event, control)}
                onPointerMove={handlePointerMove}
                onPointerUp={(event) => finishPointerDrag(event, true)}
              />
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className={cn(
                "editor-v3-table-action-strip",
                `editor-v3-table-action-strip--${control.type}`,
              )}
              container={overlayHost}
              data-editor-v3-table-action-strip="true"
              side={control.type === "row" ? "right" : "bottom"}
              sideOffset={6}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              {actions.map((action) => (
                <TableActionButton
                  key={action.label}
                  label={action.label}
                  variant={action.variant}
                  onMouseDown={() => runTableCommand(view, action.command)}
                >
                  {action.icon}
                </TableActionButton>
              ))}
            </PopoverContent>
          </Popover>
        );
      })}
    </>
  );
};

const renderOverlay = (
  root: Root,
  overlay: HTMLElement,
  overlayHost: HTMLElement,
  view: EditorView,
) => {
  const model = createModel(view, overlayHost);
  overlay.hidden = !model;

  root.render(
    <TableControlsOverlay
      model={model}
      overlayHost={overlayHost}
      view={view}
    />,
  );
};

export const editorTableControlsPlugin = () =>
  new Plugin({
    key: tableControlsKey,
    view(view) {
      const overlayHost = view.dom.parentElement ?? view.dom;
      const overlay = document.createElement("div");
      let currentView = view;
      let destroyed = false;
      let renderScheduled = false;
      overlay.className = "editor-v3-table-overlay";
      overlay.contentEditable = "false";
      overlay.hidden = true;
      overlayHost.append(overlay);

      const root = createRoot(overlay);
      const scheduleRender = () => {
        if (destroyed || renderScheduled) {
          return;
        }

        renderScheduled = true;
        queueMicrotask(() => {
          renderScheduled = false;

          if (!destroyed) {
            renderOverlay(root, overlay, overlayHost, currentView);
          }
        });
      };

      scheduleRender();

      return {
        destroy() {
          destroyed = true;
          queueMicrotask(() => {
            root.unmount();
            overlay.remove();
          });
        },
        update(nextView) {
          currentView = nextView;
          scheduleRender();
        },
      };
    },
  });
